// Layer 3 (Transformation): GM three-function traffic light — ops, metallurgy,
// maintenance (#331). Pure derivation from seed readings, series and cases.

import type { Case } from '@/versions/v1/schema/case';
import type { SessionState } from '@/versions/v1/schema/scenario';
import type { VizTone } from '@/versions/v1/modules/_viz';
import { EQUIPMENT_HEALTH_INDEX } from '@/versions/v1/shared/bunker-hill-seed';
import { matchedCauseCategory } from '@/versions/v1/shared/role-scope';
import { formatValue, readingByIdLookup } from '@/versions/v1/shared/reading.derive';
import { seriesAt, readingsAt } from '@/versions/v1/shared/series.derive';
import { diffMinutes, formatDayLabel, shiftOf } from '@/versions/v1/shared/clock.derive';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import { SERIES_BY_ID } from '@/versions/v1/shared/bunker-hill-series';
import {
  deviationFromBasisPct,
  readingComparisonBasis,
  type ComparisonBasisRead,
} from '@/versions/v1/shared/comparison-basis';
import { READINGS } from '@/versions/v1/shared/bunker-hill-readings';
import { deviationStatusPct } from './Performance.transform';
import { formatAffectsLine } from './CaseAffects.transform';

export type FunctionHealthId = 'ops' | 'metallurgy' | 'maintenance';

export type FunctionHealthLight = 'ok' | 'warn' | 'bad';

export type FunctionHealthSparkline = {
  categories: readonly string[];
  points: readonly number[];
  references: readonly { value: number; label: string; tone: VizTone }[];
};

export type EquipmentHealthRow = {
  id: string;
  asset: string;
  healthPct: number;
};

export type FunctionHealthCard = {
  id: FunctionHealthId;
  label: string;
  light: FunctionHealthLight;
  headline: string;
  reason: string;
  sparkline: FunctionHealthSparkline;
  detailLine?: string;
  byAreaRowIds: readonly string[];
  equipmentHealthRows?: readonly EquipmentHealthRow[];
  showPlanAchievement?: boolean;
  provenanceChip?: string;
  provenanceTitle?: string;
};

export type FunctionHealthRead = {
  cards: readonly FunctionHealthCard[];
};

const OPS_BY_AREA_IDS = ['area-grind'] as const;
const MET_BY_AREA_IDS = [
  'area-pb-rec',
  'area-pb-grade',
  'area-ag-rec',
  'area-ag-grade',
  'area-zn-rec',
  'area-zn-grade',
] as const;
const FILTER_BY_AREA_ID = 'area-filter';

const READING_SERIES_ID: Record<string, string> = {
  'rd-kpi-pb-rec': 'rs-pb-rec',
  'rd-kpi-zn-rec': 'rs-zn-rec',
  'rd-kpi-ag-rec': 'rs-ag-rec',
  'rd-kpi-pb-grade': 'rs-pb-grade',
  'rd-kpi-zn-grade': 'rs-zn-grade',
  'rd-kpi-ag-grade': 'rs-ag-grade',
  // The Metallurgy card's filter-area row (`FILTER_BY_AREA_ID`), a measure of
  // this function like the recoveries and grades beside it (F2 #728).
  'rd-unit-flzn-moist': 'rs-flzn-moist',
};

function isOpenCase(caseItem: Case): boolean {
  return !['approved', 'rejected', 'closed'].includes(caseItem.state);
}

function comparisonBasis(id: string, state: SessionState): ComparisonBasisRead | null {
  const projected = readingsAt(state).find((r) => r.id === id);
  const reading = projected ?? READINGS.find((r) => r.id === id);
  if (!reading) return null;
  return readingComparisonBasis(reading);
}

function kpiValueState(id: string, state: SessionState): number {
  const projected = readingsAt(state).find((r) => r.id === id);
  if (projected?.value !== undefined) return projected.value;
  const seed = readingByIdLookup(id);
  return seed?.value ?? 0;
}

function oreTdBasis(state: SessionState): ComparisonBasisRead {
  const read = comparisonBasis('rd-kpi-ore-td', state);
  if (!read) throw new Error('Missing ore-td comparison basis');
  return read;
}

function deviationLight(actual: number, target: number, higherIsBetter = true): FunctionHealthLight {
  const status = higherIsBetter
    ? deviationStatusPct(actual, target)
    : deviationStatusPct(target, actual);
  if (status === 'ok') return 'ok';
  if (status === 'watch') return 'warn';
  return 'bad';
}

function worstLight(lights: FunctionHealthLight[]): FunctionHealthLight {
  if (lights.includes('bad')) return 'bad';
  if (lights.includes('warn')) return 'warn';
  return 'ok';
}

/** One measure a function-health card is accountable for, read at the cursor
 *  against its own basis. `adversePct` is the signed deviation **in the
 *  unfavourable direction**: positive when the measure is worse than its basis
 *  — below basis for a higher-is-better measure (recovery, grade), above basis
 *  for a lower-is-better one (filter moisture) — and negative when it is
 *  better. It is a percentage of the basis, so measures in different units are
 *  ranked on one scale. */
export type AdverseMeasureRead = {
  id: string;
  higherIsBetter: boolean;
  adversePct: number;
};

/** The worst measure is the **most adverse signed deviation** (F2 #728):
 *  never the largest magnitude, which ranks a favourable move as a problem,
 *  and never the first of a tie on the traffic light, which named Pb recovery
 *  at +10 pt above budget. Highest `adversePct` wins, so with any measure short
 *  of its basis that measure is picked; with every measure on the right side of
 *  its basis the pick is the least headroom. A tie keeps the earlier candidate
 *  (the caller's list order is stable). */
export function mostAdverseMeasure(
  reads: readonly AdverseMeasureRead[],
): AdverseMeasureRead | null {
  let worst: AdverseMeasureRead | null = null;
  for (const read of reads) {
    if (!worst || read.adversePct > worst.adversePct) worst = read;
  }
  return worst;
}

function formatNumber(n: number, decimals = 0): string {
  return n.toLocaleString('en-US', {
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals,
  });
}

function thinAxisLabels(labels: readonly string[]): string[] {
  if (labels.length <= 3) return [...labels];
  const last = labels.length - 1;
  const mid = Math.floor(last / 2);
  return labels.map((label, index) => (index === 0 || index === mid || index === last ? label : ''));
}

function categoryLabel(seriesId: string, at: SeriesTime): string {
  const cadence = SERIES_BY_ID[seriesId]?.cadence;
  if (cadence === 'shift') {
    return shiftOf(at).kind === 'day' ? 'Day' : 'Night';
  }
  return formatDayLabel(at);
}

function referencesForSparkline(
  points: readonly number[],
  references: readonly { value: number; label: string; tone: VizTone }[],
): { value: number; label: string; tone: VizTone }[] {
  if (points.length === 0) return [...references];
  const min = Math.min(...points);
  const max = Math.max(...points);
  return references.map((ref) => ({
    ...ref,
    label: ref.value >= min && ref.value <= max ? '' : ref.label,
  }));
}

function sparklineFromSeries(
  seriesId: string,
  state: SessionState,
  readingId: string,
  points = 7,
): FunctionHealthSparkline {
  const view = seriesAt(seriesId, state);
  const numeric = view.points.filter(
    (p): p is { at: SeriesTime; value: number } => p.value !== null,
  );
  const slice = numeric.slice(-points);
  const categories = thinAxisLabels(slice.map((p) => categoryLabel(seriesId, p.at)));
  const pts = slice.map((p) => p.value);
  const basis = comparisonBasis(readingId, state);
  const references: { value: number; label: string; tone: VizTone }[] = basis
    ? [{ value: basis.value, label: basis.label, tone: 'neutral' }]
    : [];
  return { categories, points: pts, references: referencesForSparkline(pts, references) };
}

function hourlySparkline(
  seriesId: string,
  state: SessionState,
  readingId: string,
  hours = 48,
): FunctionHealthSparkline {
  const view = seriesAt(seriesId, state);
  const cursor = state.cursor;
  const numeric = view.points.filter(
    (p): p is { at: SeriesTime; value: number } =>
      p.value !== null &&
      diffMinutes(p.at, cursor) <= 0 &&
      diffMinutes(p.at, cursor) >= -hours * 60,
  );
  const slice = numeric;
  const categories = thinAxisLabels(
    slice.map((p) => {
      const hh = p.at.split('T')[1]?.slice(0, 5) ?? '';
      return hh;
    }),
  );
  const pts = slice.map((p) => p.value);
  const reading = readingByIdLookup(readingId);
  const references: { value: number; label: string; tone: VizTone }[] = [];
  if (reading?.band?.hi !== undefined) {
    references.push({ value: reading.band.hi, label: 'Warn', tone: 'warn' });
  }
  if (reading?.spec?.hi !== undefined) {
    references.push({ value: reading.spec.hi, label: 'Limit', tone: 'bad' });
  }
  return { categories, points: pts, references: referencesForSparkline(pts, references) };
}

function equipmentHealthRows(): EquipmentHealthRow[] {
  return EQUIPMENT_HEALTH_INDEX.map((row) => ({
    id: row.id,
    asset: row.asset,
    healthPct: row.healthPct,
  }));
}

function healthIndexLight(healthPct: number, band: { warn: number; bad: number }): FunctionHealthLight {
  if (healthPct < band.bad) return 'bad';
  if (healthPct < band.warn) return 'warn';
  return 'ok';
}

function openOpsCaseCount(cases: readonly Case[]): number {
  return cases.filter(
    (c) =>
      isOpenCase(c) &&
      c.assignedTo !== 'maintenance' &&
      matchedCauseCategory(c) !== 'mechanical-electrical' &&
      c.assignedTo !== 'metallurgist' &&
      matchedCauseCategory(c) !== 'metallurgical-process',
  ).length;
}

function openMaintenanceCaseCount(cases: readonly Case[]): number {
  return cases.filter(
    (c) =>
      isOpenCase(c) &&
      (c.assignedTo === 'maintenance' || matchedCauseCategory(c) === 'mechanical-electrical'),
  ).length;
}

export function functionHealthRead(state: SessionState, cases: readonly Case[]): FunctionHealthRead {
  const actualTd = kpiValueState('rd-kpi-ore-td', state);
  const tdBasis = oreTdBasis(state);
  const millUtil = kpiValueState('rd-kpi-mill-util', state);
  const millBasis = comparisonBasis('rd-kpi-mill-util', state);
  const millTarget = millBasis?.value ?? millUtil;
  const opsLights = [
    deviationLight(actualTd, tdBasis.value),
    deviationLight(millUtil, millTarget),
  ];
  const opsLight = worstLight(opsLights);
  const opsOpen = openOpsCaseCount(cases);

  // The measures the Metallurgy card is accountable for — its disclosure lists
  // exactly these rows (`MET_BY_AREA_IDS` plus the filter area's moisture row):
  // recovery and concentrate grades higher-is-better, filter cake moisture
  // lower-is-better. The card's light and its "Worst deviation" pick read the
  // same list, so the headline cannot name a different measure from the one the
  // card's own detail flags (F2 #728).
  const metSpecs = [
    { id: 'rd-kpi-pb-rec', higher: true },
    { id: 'rd-kpi-zn-rec', higher: true },
    { id: 'rd-kpi-ag-rec', higher: true },
    { id: 'rd-kpi-pb-grade', higher: true },
    { id: 'rd-kpi-zn-grade', higher: true },
    { id: 'rd-kpi-ag-grade', higher: true },
    { id: 'rd-unit-flzn-moist', higher: false },
  ];
  const metReads = metSpecs.map(({ id, higher }) => {
    const basis = comparisonBasis(id, state);
    const actual = kpiValueState(id, state);
    const light: FunctionHealthLight = basis
      ? deviationLight(actual, basis.value, higher)
      : 'ok';
    const pct = basis ? deviationFromBasisPct(actual, basis) : 0;
    return {
      id,
      higherIsBetter: higher,
      light,
      adversePct: higher ? -pct : pct,
    };
  });
  const metLight = worstLight(metReads.map((read) => read.light));
  // "Worst" is the most adverse signed deviation, by each measure's own
  // direction — not the largest magnitude and not first-wins on the light.
  const worstMet = mostAdverseMeasure(metReads) ?? metReads[0];
  const worstMetId = worstMet.id;
  const worstMetSeries = READING_SERIES_ID[worstMetId];
  const worstMetReading = readingByIdLookup(worstMetId);
  const worstMetValue = kpiValueState(worstMetId, state);

  const healthRows = equipmentHealthRows();
  const healthSeed = EQUIPMENT_HEALTH_INDEX.reduce(
    (min, row) => (row.healthPct < min.healthPct ? row : min),
    EQUIPMENT_HEALTH_INDEX[0],
  );
  const maintLight = healthIndexLight(healthSeed.healthPct, healthSeed.band);
  const maintOpen = openMaintenanceCaseCount(cases);
  const bmCase = cases.find((c) => c.id === 'case-bm-brg');
  const maintDetail = bmCase?.affects ? formatAffectsLine(bmCase, state) : undefined;
  const bearingTemp = seriesAt('rs-bm-brg-temp', state).latest?.value;
  const bearingReading = readingByIdLookup('rd-bm-brg-temp');

  return {
    cards: [
      {
        id: 'ops',
        label: 'Ops',
        light: opsLight,
        headline: `${formatNumber(actualTd)} dst/d`,
        reason:
          opsOpen > 0
            ? `${formatNumber(millUtil, 1)}% mill util · ${opsOpen} open production case${opsOpen === 1 ? '' : 's'}`
            : `${formatNumber(millUtil, 1)}% mill util vs ${millBasis?.label.toLowerCase() ?? 'plan'}`,
        sparkline: sparklineFromSeries('rs-ore-td', state, 'rd-kpi-ore-td'),
        byAreaRowIds: [...OPS_BY_AREA_IDS],
        showPlanAchievement: true,
        provenanceChip: '[syn]',
        provenanceTitle: tdBasis.label,
      },
      {
        id: 'metallurgy',
        label: 'Metallurgy',
        light: metLight,
        headline: worstMetReading
          ? formatValue(worstMetReading, { value: worstMetValue, withUnit: true })
          : formatNumber(worstMetValue, 1),
        reason: `Worst deviation · ${worstMetReading?.label ?? 'recovery'}`,
        sparkline: sparklineFromSeries(worstMetSeries, state, worstMetId),
        byAreaRowIds: [...MET_BY_AREA_IDS, FILTER_BY_AREA_ID],
        provenanceChip: '[syn]',
        provenanceTitle: worstMetReading?.provenance,
      },
      {
        id: 'maintenance',
        label: 'Maintenance',
        light: maintLight,
        headline:
          bearingTemp !== undefined && bearingReading
            ? formatValue(bearingReading, { value: bearingTemp, withUnit: true })
            : '—',
        reason: `${healthSeed.asset} lowest · ${healthSeed.healthPct}% health index${maintOpen > 0 ? ` · ${maintOpen} open maintenance case${maintOpen === 1 ? '' : 's'}` : ''}`,
        sparkline: hourlySparkline('rs-bm-brg-temp', state, 'rd-bm-brg-temp', 48),
        detailLine: maintDetail,
        byAreaRowIds: [],
        equipmentHealthRows: healthRows,
        provenanceChip: '[syn]',
        provenanceTitle: bearingReading?.provenance,
      },
    ],
  };
}

/** Y domain for a function-health sparkline with room above and below the
 * points and reference lines, so a reference label clears the plot edge and
 * the tick row instead of clipping or overprinting it. A reference that sits
 * beyond every point (a Budget under the line, a Limit above it) gets the wide
 * side of the room, where its label draws; the far side keeps a narrow margin.
 * The total room is the same, so the curve keeps its share of the plot. */
export function sparklineYDomain(sparkline: FunctionHealthSparkline, headroom = 0.6): readonly [number, number] {
  const values = [...sparkline.points, ...sparkline.references.map((ref) => ref.value)];
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const span = hi - lo || Math.abs(hi) || 1;
  const pointsLo = Math.min(...sparkline.points);
  const pointsHi = Math.max(...sparkline.points);
  const refBelow = sparkline.references.some((ref) => ref.value < pointsLo);
  const refAbove = sparkline.references.some((ref) => ref.value > pointsHi);
  const wide = headroom * 1.5;
  const narrow = headroom * 0.5;
  if (refBelow && !refAbove) return [lo - span * wide, hi + span * narrow];
  if (refAbove && !refBelow) return [lo - span * narrow, hi + span * wide];
  return [lo - span * headroom, hi + span * headroom];
}
